import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  useActivities,
  useGoals,
  usePlans,
  useCreateActivity,
  useCompleteActivity,
} from "@/hooks/useCareer";
import { Modal } from "@/components/ui/modal";
import {
  FilterChips,
  StatusChip,
  fieldClassName,
} from "@/components/goals/goals-ui";
import { formatConfidence, isOpenWork } from "@/lib/goals-progress";

type WorkFilter = "open" | "done" | "all";

export function WorkListPage() {
  const navigate = useNavigate();
  const activitiesQuery = useActivities(false);
  const goalsQuery = useGoals();
  const plansQuery = usePlans();
  const createActivity = useCreateActivity();
  const completeActivity = useCompleteActivity();

  const [filter, setFilter] = useState<WorkFilter>("open");
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [goalId, setGoalId] = useState("");
  const [planId, setPlanId] = useState("");
  const [effortWeight, setEffortWeight] = useState("M");

  const activities = activitiesQuery.data?.activities ?? [];
  const goals = goalsQuery.data?.goals ?? [];
  const plans = plansQuery.data?.plans ?? [];

  const filtered = useMemo(() => {
    return activities.filter((a) => {
      if (filter === "open") return isOpenWork(a.state);
      if (filter === "done") return a.state === "done";
      return true;
    });
  }, [activities, filter]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    const { activity } = await createActivity.mutateAsync({
      title: title.trim(),
      type: "work",
      dueAt: dueAt ? new Date(dueAt).toISOString() : undefined,
      goalId: goalId || undefined,
      planId: planId || undefined,
      effortWeight,
    });
    setCreateOpen(false);
    setTitle("");
    setDueAt("");
    navigate(`/work/${activity.id}`);
  }

  return (
    <div className="pb-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <FilterChips
          value={filter}
          onChange={setFilter}
          options={[
            { value: "open", label: "Open" },
            { value: "done", label: "Done" },
            { value: "all", label: "All" },
          ]}
        />
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground"
        >
          New work
        </button>
      </div>

      {activitiesQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading work…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
          <p className="text-sm text-muted-foreground">No work in this filter.</p>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="mt-3 text-sm text-brand hover:underline"
          >
            Add work
          </button>
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((a) => {
            const goal = goals.find((g) => g.id === a.goalId);
            const plan = plans.find((p) => p.id === a.planId);
            const conf = formatConfidence(a.confidence);
            return (
              <li
                key={a.id}
                className="flex items-start gap-2 rounded-md border border-border/80 px-3 py-2.5 text-sm transition hover:border-border hover:bg-surface-hover"
              >
                <Link to={`/work/${a.id}`} className="min-w-0 flex-1">
                  <p className="font-medium text-foreground">{a.title}</p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <StatusChip>{a.state}</StatusChip>
                    {a.dueAt ? (
                      <span>due {new Date(a.dueAt).toLocaleDateString()}</span>
                    ) : null}
                    {plan ? (
                      <span>→ {plan.title}</span>
                    ) : goal ? (
                      <span>→ {goal.title}</span>
                    ) : null}
                    {conf ? <span>{conf} confidence</span> : null}
                  </div>
                </Link>
                {isOpenWork(a.state) ? (
                  <button
                    type="button"
                    className="shrink-0 text-xs text-brand hover:underline"
                    onClick={() => void completeActivity.mutateAsync({ id: a.id })}
                  >
                    Done
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} labelledBy="new-work-title">
        <form onSubmit={(e) => void handleCreate(e)} className="flex flex-col">
          <div className="border-b border-border px-4 py-3">
            <h2 id="new-work-title" className="text-base font-semibold">
              New work
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Epic-sized chunk of effort — not a checklist item.
            </p>
          </div>
          <div className="space-y-3 overflow-y-auto px-4 py-4">
            <input
              className={fieldClassName()}
              placeholder="Work title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              required
            />
            <input
              type="datetime-local"
              className={fieldClassName()}
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
            />
            <select
              className={fieldClassName()}
              value={effortWeight}
              onChange={(e) => setEffortWeight(e.target.value)}
            >
              <option value="S">Size S</option>
              <option value="M">Size M</option>
              <option value="L">Size L</option>
            </select>
            <select
              className={fieldClassName()}
              value={goalId}
              onChange={(e) => setGoalId(e.target.value)}
            >
              <option value="">No linked goal</option>
              {goals.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title}
                </option>
              ))}
            </select>
            <select
              className={fieldClassName()}
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
            >
              <option value="">No linked plan</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="rounded-md px-3 py-1.5 text-sm text-muted-foreground"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createActivity.isPending}
              className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
            >
              Create
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
